'use client';

import { m } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import React from 'react';
import Link from 'next/link';

export const Hero = () => {
    return (
        <section className="relative pt-28 lg:pt-36 pb-0 overflow-hidden bg-white">
            {/* Subtle ambient */}
            <div className="absolute top-0 right-0 w-[600px] h-[500px] bg-orange-50/40 rounded-full blur-3xl pointer-events-none -z-0" />

            <div className="max-w-6xl mx-auto px-6 relative z-10">

                {/* Badge */}
                <m.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="inline-flex items-center gap-2 px-3.5 py-1.5 mt-8 md:mt-0 bg-orange-50 border border-orange-100 rounded-full text-sm text-orange-700 font-medium mb-10 md:mb-8"
                >
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" />
                    500+ brands on autopilot
                </m.div>

                {/* Left-aligned headline — Linear style */}
                <m.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.55, delay: 0.05 }}
                    className="text-5xl sm:text-6xl lg:text-[5.5rem] font-bold text-neutral-900 mb-6 leading-[1.05] tracking-tighter max-w-4xl"
                >
                    AI Agents for Customer Acquisition
                </m.h1>

                {/* Left-aligned two-column subtext + CTA — exactly like Linear */}
                <m.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.55, delay: 0.12 }}
                    className="flex flex-col sm:flex-row sm:items-end justify-between gap-8 mb-12"
                >
                    {/* Left: subtext (Linear has this on the left) */}
                    <div className="max-w-xl">
                        <p className="text-neutral-500 text-lg leading-relaxed">
                            AI agents that find leads, create content, and close deals while you sleep.<br />
                            Built for the autonomous era — it strategizes, pivots, and grows your business autonomously.
                        </p>
                    </div>

                    {/* Right: CTA buttons (Linear has a pill link on the right) */}
                    <div className="flex items-center gap-3 shrink-0">
                        <Link href="/login?mode=signup">
                            <m.button
                                className="px-6 py-3 bg-neutral-900 text-white font-semibold rounded-lg hover:bg-neutral-800 transition-colors flex items-center gap-2 group text-sm cursor-pointer"
                                whileHover={{ scale: 1.01 }}
                                whileTap={{ scale: 0.99 }}
                            >
                                Get started free
                                <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
                            </m.button>
                        </Link>
                        <Link href="#features">
                            <button className="px-6 py-3 border border-neutral-200 hover:border-neutral-300 text-neutral-600 font-semibold rounded-lg transition-colors text-sm cursor-pointer hover:bg-neutral-50">
                                See how it works
                            </button>
                        </Link>
                    </div>
                </m.div>

                {/* Product screenshot — full-width flush to bottom, exactly like Linear */}
                <m.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                    className="relative mx-auto w-full"
                >
                    <div className="rounded-t-2xl border border-neutral-200 border-b-0 shadow-[0_-8px_60px_-10px_rgba(0,0,0,0.10)] overflow-hidden bg-[#FAFAFA]">
                        {/* macOS titlebar */}
                        <div className="flex items-center gap-2 px-5 py-3 border-b border-neutral-100 bg-white shrink-0">
                            <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                            <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                            <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
                            <div className="flex-1 flex justify-center">
                                <div className="px-8 py-1 rounded-md bg-neutral-50 border border-neutral-100 text-xs text-neutral-400 font-medium hidden sm:block">
                                    axist.app/dashboard
                                </div>
                            </div>
                        </div>

                        {/* Dashboard body */}
                        <div className="flex min-h-[300px] sm:min-h-[380px] lg:min-h-[440px] text-left overflow-hidden">
                            {/* Sidebar — hidden on mobile, narrow on tablet, full on desktop */}
                            <div className="hidden sm:flex w-36 lg:w-44 border-r border-neutral-100 bg-white p-4 flex-col gap-1 shrink-0">
                                <div className="h-5 rounded-md bg-orange-500/15 mb-3 w-full" />
                                {['Scout', 'Quill', 'Archer', 'Trader', 'Iris', 'Fixer'].map((a, i) => (
                                    <div key={a} className={`flex items-center gap-2 px-2 py-1.5 rounded-md ${i === 0 ? 'bg-neutral-100' : ''}`}>
                                        <div className={`w-2 h-2 rounded-full ${i === 0 ? 'bg-emerald-500' :
                                                i === 1 ? 'bg-blue-400' :
                                                    i === 2 ? 'bg-orange-400' :
                                                        i === 3 ? 'bg-purple-400' :
                                                            i === 4 ? 'bg-pink-400' : 'bg-neutral-300'
                                            }`} />
                                        <span className="text-[11px] text-neutral-500 font-medium">{a}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Main content */}
                            <div className="flex-1 p-4 sm:p-6 space-y-4 min-w-0">
                                {/* KPI row — 2 on mobile, 4 on desktop */}
                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                    {[
                                        { label: 'Leads Found', val: '847', color: 'bg-emerald-50 border-emerald-100', text: 'text-emerald-700' },
                                        { label: 'Posts Published', val: '23', color: 'bg-blue-50 border-blue-100', text: 'text-blue-700' },
                                        { label: 'Calls Booked', val: '12', color: 'bg-purple-50 border-purple-100', text: 'text-purple-700' },
                                        { label: 'Revenue Pipeline', val: '$48K', color: 'bg-orange-50 border-orange-100', text: 'text-orange-700' },
                                    ].map(kpi => (
                                        <div key={kpi.label} className={`rounded-xl border ${kpi.color} p-3`}>
                                            <p className="text-[10px] text-neutral-400 mb-1 leading-tight">{kpi.label}</p>
                                            <p className={`text-xl font-bold ${kpi.text}`}>{kpi.val}</p>
                                        </div>
                                    ))}
                                </div>

                                {/* Live activity feed */}
                                <div className="rounded-xl border border-neutral-100 bg-white overflow-hidden">
                                    <div className="flex items-center justify-between px-4 py-2.5 border-b border-neutral-50">
                                        <span className="text-[11px] font-semibold text-neutral-600">Live Activity</span>
                                        <span className="flex items-center gap-1 text-[10px] text-emerald-600 font-medium">
                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />Running
                                        </span>
                                    </div>
                                    {[
                                        { agent: 'Scout', action: 'Found 12 high-intent leads on LinkedIn', time: '2m ago', dot: 'bg-emerald-400' },
                                        { agent: 'Quill', action: 'Published LinkedIn post — 340 impressions', time: '18m ago', dot: 'bg-blue-400' },
                                        { agent: 'Archer', action: 'Sent outreach to 8 leads, 3 replied', time: '34m ago', dot: 'bg-orange-400' },
                                    ].map(row => (
                                        <div key={row.action} className="flex items-center gap-3 px-4 py-2.5 border-b border-neutral-50 last:border-0">
                                            <span className={`w-2 h-2 rounded-full ${row.dot} shrink-0`} />
                                            <span className="text-[11px] font-semibold text-neutral-700 shrink-0 w-10 sm:w-12">{row.agent}</span>
                                            <span className="text-[11px] text-neutral-500 flex-1 min-w-0 truncate">{row.action}</span>
                                            <span className="text-[10px] text-neutral-400 shrink-0 hidden sm:block">{row.time}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Autopilot status bar */}
                                <div className="rounded-xl bg-neutral-900 px-4 py-3 flex items-center justify-between">
                                    <div className="flex items-center gap-2.5">
                                        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                                        <span className="text-white text-[12px] font-semibold">Autopilot Active</span>
                                        <span className="text-neutral-400 text-[11px] hidden sm:block">— 6 agents running autonomously</span>
                                    </div>
                                    <span className="text-neutral-500 text-[11px] font-mono">24/7</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </m.div>
            </div>
        </section>
    );
};
